/**
 * motion.ts — sistema de movimiento CONSALUD v2027 (anime.js 4.5 + animate.css 4.1).
 *
 * Copiar a consaludenarm-platform/src/lib/motion.ts.
 * Requiere:  npm i animejs@^4.5.0 animate.css@^4.1.1
 * y en src/styles/index.css:  @import "animate.css";
 *
 * Reglas (ver references/animaciones.md):
 *  - Toda animación de anime.js se crea dentro de createScope({ root }) y se revierte al desmontar.
 *  - Con prefers-reduced-motion solo hay fundidos cortos (150 ms), sin desplazamientos.
 *  - Solo se animan transform y opacity.
 */
import { useEffect, useRef, useState, type DependencyList, type RefObject } from 'react';
import {
  animate,
  createDraggable,
  createDrawable,
  createScope,
  createSpring,
  createTimeline,
  onScroll,
  stagger,
  utils,
  type Scope,
} from 'animejs';

/* ------------------------------------------------------------------ tokens */

export const DUR = { instante: 120, rapida: 200, base: 300, entrada: 600, celebracion: 1000 } as const;
export const EASE = { entrada: 'outExpo', estado: 'inOutQuad', jugueton: 'outBack(1.4)' } as const;
export const RESORTE = createSpring({ stiffness: 180, damping: 18 });
export const MARCA = ['#005DB9', '#02D1D5', '#6BBF49', '#720294', '#2E99DA', '#F5C300'];

const REDUCE_QUERY = '(prefers-reduced-motion: reduce)';

export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(REDUCE_QUERY).matches;
}

/* ------------------------------------------------------------- scope hook */

/**
 * Crea un scope de anime.js ligado a `root` y lo revierte al desmontar o al cambiar deps.
 * El callback recibe `reduce` (true si el usuario pidió menos movimiento).
 */
export function useAnimeScope(
  root: RefObject<HTMLElement | null>,
  build: (reduce: boolean, scope: Scope) => void,
  deps: DependencyList = [],
) {
  const scopeRef = useRef<Scope | null>(null);
  useEffect(() => {
    if (!root.current) return;
    const scope = createScope({ root: root as RefObject<HTMLElement>, mediaQueries: { reduce: REDUCE_QUERY } });
    scope.add((self) => {
      build(Boolean(self?.matches.reduce), scope);
    });
    scopeRef.current = scope;
    return () => scope.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return scopeRef;
}

/* ---------------------------------------------------------- animate.css */

export type AnimateCssName =
  | 'fadeIn' | 'fadeInUp' | 'fadeInDown' | 'fadeOut' | 'zoomIn' | 'zoomOut'
  | 'bounceIn' | 'headShake' | 'tada' | 'pulse' | 'heartBeat' | 'rotateInDownLeft' | 'flipInX';

/**
 * Aplica una animación de animate.css a un elemento y resuelve al terminar.
 * animateCSS(el, 'headShake')  ·  animateCSS(el, 'fadeInUp', { duration: 400 })
 */
export function animateCSS(
  el: Element | null | undefined,
  name: AnimateCssName,
  { duration, prefix = 'animate__' }: { duration?: number; prefix?: string } = {},
): Promise<void> {
  return new Promise((resolve) => {
    if (!el) return resolve();
    const node = el as HTMLElement;
    const classes = [`${prefix}animated`, `${prefix}${name}`];
    if (duration) node.style.setProperty('--animate-duration', `${duration}ms`);
    node.classList.add(...classes);
    const done = (e: Event) => {
      e.stopPropagation();
      node.classList.remove(...classes);
      if (duration) node.style.removeProperty('--animate-duration');
      resolve();
    };
    node.addEventListener('animationend', done, { once: true });
  });
}

/* ------------------------------------------------------- entradas y listas */

/** Entrada de página/ruta: opacity + y 8 → 0. */
export function usePageEnter(root: RefObject<HTMLElement | null>, deps: DependencyList = []) {
  useAnimeScope(root, (reduce) => {
    const el = root.current!;
    animate(el, reduce
      ? { opacity: { from: 0 }, duration: 150 }
      : { opacity: { from: 0 }, y: { from: 8 }, duration: DUR.base, ease: EASE.entrada });
  }, deps);
}

/**
 * Entrada escalonada de los hijos que coincidan con `selector` dentro de `root`.
 * useStagger(ref, '[data-card]', { step: 80 })  ·  rejillas: { grid: [cols, rows] }
 */
export function useStagger(
  root: RefObject<HTMLElement | null>,
  selector: string,
  { step = 40, y = 24, max = 15, grid, deps = [] }: { step?: number; y?: number; max?: number; grid?: [number, number]; deps?: DependencyList } = {},
) {
  useAnimeScope(root, (reduce) => {
    const all = Array.from(root.current!.querySelectorAll<HTMLElement>(selector));
    const animated = all.slice(0, max);
    if (!animated.length) return;
    animate(animated, reduce
      ? { opacity: { from: 0 }, duration: 150 }
      : {
          opacity: { from: 0 },
          y: { from: y },
          duration: DUR.entrada,
          ease: EASE.entrada,
          delay: stagger(step, grid ? { grid, from: 'first' } : undefined),
        });
  }, deps);
}

/** Revela al hacer scroll los elementos [data-reveal] dentro de root (una sola vez). */
export function useReveal(root: RefObject<HTMLElement | null>, deps: DependencyList = []) {
  useAnimeScope(root, (reduce) => {
    root.current!.querySelectorAll<HTMLElement>('[data-reveal]').forEach((el) => {
      animate(el, {
        opacity: { from: 0 },
        ...(reduce ? {} : { y: { from: 32 } }),
        duration: reduce ? 150 : DUR.entrada,
        ease: EASE.entrada,
        autoplay: onScroll({ target: el, enter: 'bottom top', repeat: false }),
      });
    });
  }, deps);
}

/* ------------------------------------------------------------------ números */

const fmtNumero = (v: number, decimals: number) =>
  v.toLocaleString('es-MX', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
const fmtMXN = (v: number) => v.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });

/**
 * Cuenta de `from` a `to` escribiendo el texto del elemento.
 * useCountUp(ref, 33, { suffix: '%' })  ·  useCountUp(ref, 3999, { from: 4999, currency: true })
 */
export function useCountUp(
  ref: RefObject<HTMLElement | null>,
  to: number,
  { from = 0, duration = DUR.celebracion, decimals = 0, suffix = '', currency = false }:
    { from?: number; duration?: number; decimals?: number; suffix?: string; currency?: boolean } = {},
) {
  useAnimeScope(ref, (reduce) => {
    const el = ref.current!;
    const write = (v: number) => { el.textContent = currency ? fmtMXN(v) : fmtNumero(v, decimals) + suffix; };
    if (reduce) return write(to);
    const state = { v: from };
    animate(state, { v: to, duration, ease: EASE.entrada, onUpdate: () => write(state.v) });
  }, [to, from]);
}

/**
 * Cuenta regresiva al ENARM (la fecha sale de GET /catalogs/enarm, nunca hardcodeada).
 * const { dias, horas, minutos, segundos } = useCountdown(fechaEnarm)
 */
export function useCountdown(objetivo: Date | string | null | undefined) {
  const calc = () => {
    const ms = Math.max(0, (objetivo ? new Date(objetivo).getTime() : 0) - Date.now());
    return { dias: Math.floor(ms / 864e5), horas: Math.floor(ms / 36e5) % 24, minutos: Math.floor(ms / 6e4) % 60, segundos: Math.floor(ms / 1e3) % 60, terminado: ms === 0 };
  };
  const [t, setT] = useState(calc);
  useEffect(() => {
    setT(calc());
    const id = window.setInterval(() => setT(calc()), 1000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objetivo]);
  return t;
}

/** Mini "flip" de un dígito (cuenta regresiva ENARM). Llamar cuando cambie el valor. */
export function flipDigit(el: HTMLElement | null) {
  if (!el || prefersReducedMotion()) return;
  animate(el, { y: { from: '-60%' }, opacity: { from: 0 }, duration: DUR.rapida, ease: 'outQuad' });
}

/** Rebote de un número (stepper del simulador). */
export function bump(el: HTMLElement | null) {
  if (!el || prefersReducedMotion()) return;
  animate(el, { scale: [{ to: 1.25, duration: 90 }, { to: 1, duration: 220, ease: EASE.jugueton }] });
}

/* ---------------------------------------------------------------------- SVG */

/**
 * Anillo de progreso: `ref` apunta a un <circle> (o <path>) con stroke.
 * <svg viewBox="0 0 120 120"><circle ref={ring} cx="60" cy="60" r="52" pathLength="1"
 *   fill="none" stroke="#1CD0C7" strokeWidth="12" strokeLinecap="round" transform="rotate(-90 60 60)" /></svg>
 */
export function useProgressRing(ref: RefObject<SVGGeometryElement | null>, pct: number) {
  useAnimeScope(ref as unknown as RefObject<HTMLElement | null>, (reduce) => {
    const [drawable] = createDrawable(ref.current!);
    if (!drawable) return;
    const end = Math.max(0, Math.min(1, pct / 100));
    animate(drawable, { draw: [`0 0`, `0 ${end}`], duration: reduce ? 0 : DUR.celebracion, ease: EASE.entrada });
  }, [pct]);
}

/** Barra de progreso que crece (scaleX) al hacerse visible. El elemento debe tener transform-origin: left. */
export function useRevealProgress(ref: RefObject<HTMLElement | null>, pct: number) {
  useAnimeScope(ref, (reduce) => {
    const el = ref.current!;
    const to = Math.max(0, Math.min(1, pct / 100));
    animate(el, {
      scaleX: { from: 0, to },
      duration: reduce ? 0 : DUR.celebracion,
      ease: EASE.entrada,
      autoplay: reduce ? true : onScroll({ target: el, enter: 'bottom top', repeat: false }),
    });
  }, [pct]);
}

/** Dibuja un check de éxito (path SVG). */
export function drawCheck(path: SVGGeometryElement | null) {
  if (!path) return;
  const [drawable] = createDrawable(path);
  if (!drawable) return;
  animate(drawable, { draw: ['0 0', '0 1'], duration: prefersReducedMotion() ? 0 : 500, ease: 'outQuad' });
}

/**
 * Cargador "trazo del cable": `cable` es el path del cable de la N, `enchufe` el grupo del enchufe.
 * Devuelve la animación para poder detenerla (anim.pause() / anim.revert()).
 */
export function cableLoader(cable: SVGGeometryElement, enchufe?: SVGElement | null) {
  const [drawable] = createDrawable(cable);
  const tl = createTimeline({ loop: true, defaults: { ease: 'inOutQuad' } });
  if (!drawable) return tl;
  tl.add(drawable, { draw: ['0 0', '0 1'], duration: 1100 });
  if (enchufe) tl.add(enchufe, { scale: [{ to: 1.15, duration: 150 }, { to: 1, duration: 200 }], opacity: [{ to: 1 }] }, '-=100');
  tl.add(drawable, { draw: ['0 1', '1 1'], duration: 350 });
  return tl;
}

/* ------------------------------------------------------------ estudio */

/** Feedback al presionar una opción A–D. */
export function pressFeedback(el: HTMLElement | null) {
  if (!el || prefersReducedMotion()) return;
  animate(el, { scale: [{ to: 0.97, duration: 80 }, { to: 1, duration: 160, ease: 'outQuad' }] });
}

/**
 * Revela la respuesta: color ya aplicado por React (clase correcta/incorrecta) → ícono → justificación.
 */
export function answerFeedback(
  option: HTMLElement | null,
  { correct, icon, explanation }: { correct: boolean; icon?: HTMLElement | null; explanation?: HTMLElement | null },
) {
  if (!option) return;
  const reduce = prefersReducedMotion();
  if (!correct) void animateCSS(option, 'headShake', { duration: 500 });
  if (icon) void animateCSS(icon, 'bounceIn', { duration: 500 });
  if (explanation) {
    animate(explanation, reduce
      ? { opacity: { from: 0 }, duration: 150 }
      : { opacity: { from: 0 }, y: { from: -8 }, duration: DUR.base, delay: 150, ease: EASE.entrada });
  }
}

/** Cambia de caso clínico: el actual sale hacia `dir` y el nuevo entra desde el lado opuesto. */
export function swapCase(oldEl: HTMLElement | null, newEl: HTMLElement | null, dir: 1 | -1 = 1) {
  const reduce = prefersReducedMotion();
  const d = reduce ? 0 : 24 * dir;
  const tl = createTimeline({ defaults: { duration: reduce ? 120 : 250, ease: EASE.estado } });
  if (oldEl) tl.add(oldEl, { x: { to: -d }, opacity: { to: 0 } });
  if (newEl) tl.add(newEl, { x: { from: d, to: 0 }, opacity: { from: 0, to: 1 } });
  return tl;
}

/** Voltea una flashcard (el elemento debe tener transform-style: preserve-3d). */
export function flipCard(el: HTMLElement | null, back: boolean) {
  if (!el) return;
  animate(el, { rotateY: back ? 180 : 0, duration: prefersReducedMotion() ? 0 : 500, ease: EASE.estado });
}

/**
 * Deslizar tarjetas (flashcards, láminas) en móvil. Llama a onNext/onPrev al soltar pasado el umbral.
 */
export function useSwipeCards(
  ref: RefObject<HTMLElement | null>,
  { onNext, onPrev, threshold = 80 }: { onNext: () => void; onPrev: () => void; threshold?: number },
) {
  useAnimeScope(ref, (reduce) => {
    if (reduce) return; // sin arrastre animado: se usan botones y flechas del teclado
    const drag = createDraggable(ref.current!, {
      x: true,
      y: false,
      releaseEase: RESORTE,
      onRelease: (self) => {
        if (self.x < -threshold) onNext();
        else if (self.x > threshold) onPrev();
        self.reset();
      },
    });
    return void drag;
  }, [onNext, onPrev]);
}

/** Fundido cruzado entre láminas (manuales, presentaciones). */
export function crossFade(oldEl: HTMLElement | null, newEl: HTMLElement | null) {
  const duration = prefersReducedMotion() ? 0 : DUR.rapida;
  if (oldEl) animate(oldEl, { opacity: { to: 0 }, duration });
  if (newEl) animate(newEl, { opacity: { from: 0, to: 1 }, duration });
}

/* ------------------------------------------------------------ marca */

/** Saludo de mascota: balanceo corto, una vez. */
export function waveMascot(el: HTMLElement | null) {
  if (!el || prefersReducedMotion()) return;
  utils.set(el, { transformOrigin: '50% 100%' });
  animate(el, { rotate: [{ to: 4 }, { to: -2 }, { to: 0 }], duration: 900, ease: EASE.jugueton });
}

/** Indicador deslizante de pestañas (CLASES · BIBLIOTECA · …). */
export function moveIndicator(indicator: HTMLElement | null, tab: HTMLElement | null) {
  if (!indicator || !tab) return;
  const parent = indicator.offsetParent as HTMLElement | null;
  const x = tab.offsetLeft - (parent?.scrollLeft ?? 0);
  animate(indicator, {
    x,
    width: tab.offsetWidth, // excepción controlada: un solo elemento, no una lista
    duration: prefersReducedMotion() ? 0 : DUR.base,
    ease: EASE.estado,
  });
}

/** Confeti sobrio (20 piezas, colores de marca). `container` debe tener position: relative. */
export function celebrate(container: HTMLElement | null, pieces = 20) {
  if (!container || prefersReducedMotion()) return;
  const nodes = Array.from({ length: pieces }, (_, i) => {
    const n = document.createElement('span');
    n.setAttribute('aria-hidden', 'true');
    Object.assign(n.style, {
      position: 'absolute', left: '50%', top: '40%', width: '8px', height: '14px', borderRadius: '2px',
      background: MARCA[i % MARCA.length], pointerEvents: 'none',
    });
    container.appendChild(n);
    return n;
  });
  animate(nodes, {
    x: () => utils.random(-220, 220),
    y: () => utils.random(-180, 160),
    rotate: () => utils.random(-200, 200),
    opacity: [{ to: 1, duration: 100 }, { to: 0, delay: 700, duration: 300 }],
    duration: DUR.celebracion,
    ease: 'outCubic',
    onComplete: () => nodes.forEach((n) => n.remove()),
  });
}
